import type { WorkflowStepDto } from '@/features/workflows/schemas/workflowSchemas';

import { parseTemplateStepSettings } from './templateStepSettings';

/** The template an eSign step signs, and the step that produced it. */
export interface EsignSource {
  templateId: string;
  templateStepId: string;
}

/**
 * Finds the document an eSign Template step applies to.
 *
 * It signs whatever the Document Template step BEFORE it generated — a signature
 * cannot be placed on a document that has not been produced yet, so a template step
 * later in the workflow is not a candidate. With no such step, the eSign step has
 * nothing to sign and says so rather than silently signing nothing.
 */
export function findEsignSource(
  steps: readonly WorkflowStepDto[],
  esignStepId: string,
): EsignSource | null {
  const ordered = [...steps].sort((a, b) => a.position - b.position);
  const esignIndex = ordered.findIndex((s) => s.id === esignStepId);
  const searchable = esignIndex === -1 ? ordered : ordered.slice(0, esignIndex);

  // Last one wins: with several template steps the nearest preceding document is
  // the one this signature step is about.
  for (let i = searchable.length - 1; i >= 0; i--) {
    const step = searchable[i];
    if (!step || step.stepType !== 'document_template') continue;

    const templateId = parseTemplateStepSettings(step.settings).templateId;
    if (templateId) {
      return { templateId, templateStepId: step.id };
    }
  }

  return null;
}
